/**
 * createApiClient — middleware 链组装工厂。
 *
 * 设计要点：
 *  - 洋葱模型：传入 middleware 数组按顺序包成 next 链；最外层先执行。
 *  - `compose([a, b, c])(ctx)` 实际等价于：
 *      a(ctx, () => b(ctx, () => c(ctx, terminator)))
 *  - parse 是终结器（不调 next，直接跑 fetch + envelope 解包）。
 *  - 每次请求生成新的 traceId（16 hex 字符），refresh 重试时共享同一 traceId。
 *  - request 入口负责把 ApiResponseContext 映射成 ApiError 抛出（保留旧 client 行为）。
 *  - caller 负责提供完整 middleware 数组（含 auth/refresh/parse 与可选 telemetry）。
 */

import { clearTokens, readAccess, readRefresh, writeTokens } from './tokens';
import type { ApiCallContext, ApiResponseContext, ApiClient, Middleware } from './middleware/types';
import { ApiError } from './apiError';

export interface CreateApiClientOptions {
  baseUrl: string;
  /** 完整 middleware 链（含最外层 telemetry 和最里层 parse） */
  middleware: Middleware[];
  /** 自定义 fetch（测试可注入） */
  fetchImpl?: typeof fetch;
  /** 自定义 traceId 生成器（默认 crypto.randomUUID / fallback） */
  traceIdFactory?: () => string;
}

/**
 * 把 middleware 数组组装成单一可调用函数：dispatch(ctx) → ApiResponseContext。
 * 类似 Koa 的 compose，但允许 refresh 在重试时合法地再次进入链。
 */
function compose(middlewares: Middleware[]): (ctx: ApiCallContext) => Promise<ApiResponseContext> {
  return function composed(ctx: ApiCallContext): Promise<ApiResponseContext> {
    return dispatch(0, ctx, new Set());
  };
  function dispatch(i: number, ctx: ApiCallContext, visited: Set<number>): Promise<ApiResponseContext> {
    const mw = middlewares[i];
    if (!mw) {
      return Promise.reject(new Error('middleware chain fell off the end'));
    }
    visited.add(i);
    const nextFn = (): Promise<ApiResponseContext> => {
      const nextIdx = i + 1;
      if (visited.has(nextIdx)) {
        // refresh 重试：合法；其他 middleware 重复调用 next() 报错。
        const fnName = middlewares[i]?.name ?? '';
        if (!/refresh/i.test(fnName)) {
          return Promise.reject(new Error('next() called multiple times in middleware'));
        }
      }
      return dispatch(nextIdx, ctx, visited);
    };
    return mw(ctx, nextFn);
  }
}

function defaultTraceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  }
  // 退化路径：用 Math.random 生成 16 hex 字符。
  let out = '';
  for (let i = 0; i < 16; i++) {
    out += Math.floor(Math.random() * 16).toString(16);
  }
  return out;
}

function resolveBaseUrl(): string {
  const env =
    typeof import.meta !== 'undefined'
      ? (import.meta as { env?: Record<string, string | boolean | undefined> }).env
      : undefined;

  // 开发环境必须使用相对路径，让浏览器请求进入 Vite 的 /api 代理，
  // 再由 BFF(8082) 转发到 Go API(8081)。直接拼接旧的 8080 会绕过代理，
  // 最终被前端统一识别成 NETWORK 错误。
  if (env?.DEV === true || env?.MODE === 'development') {
    return '';
  }

  return typeof env?.VITE_API_BASE === 'string' ? env.VITE_API_BASE.trim() : '';
}

/**
 * 把 ApiResponseContext 映射为 throw 或 return data。
 */
function toApiError(resp: ApiResponseContext): ApiError {
  if (resp.errorCode) {
    return new ApiError(resp.errorCode, resp.status, `request failed (status=${resp.status}, code=${resp.errorCode})`);
  }
  return new ApiError('UNKNOWN', resp.status, `unexpected response (status=${resp.status})`);
}

/**
 * 主入口：构造 ApiClient。
 *
 * `opts.middleware` 必须是完整链路（含 auth/refresh/parse 与 telemetry）。
 * client.ts 会按 [auth, refresh, telemetry, parse] 顺序组装。
 */
export function createApiClient(opts: CreateApiClientOptions): ApiClient {
  const traceId = opts.traceIdFactory ?? defaultTraceId;

  const dispatch = compose(opts.middleware);

  async function run(method: string, path: string, body?: unknown): Promise<unknown> {
    const ctx: ApiCallContext = {
      method,
      path,
      body,
      headers: {
        Accept: 'application/json',
      },
      retryCount: 0,
      traceId: traceId(),
    };
    if (body !== undefined) {
      ctx.headers['Content-Type'] = 'application/json';
    }
    const resp = await dispatch(ctx);
    if (!resp.ok || resp.errorCode) {
      throw toApiError(resp);
    }
    return resp.data;
  }

  const client: ApiClient = {
    baseUrl: opts.baseUrl,
    getAccess(): string | null {
      return readAccess();
    },
    getRefresh(): string | null {
      return readRefresh();
    },
    setTokens(access: string, refresh: string): void {
      writeTokens(access, refresh);
    },
    clearTokens(): void {
      clearTokens();
    },
    get<T>(path: string): Promise<T> {
      return run('GET', path) as Promise<T>;
    },
    post<T>(path: string, body?: unknown): Promise<T> {
      return run('POST', path, body) as Promise<T>;
    },
    patch<T>(path: string, body?: unknown): Promise<T> {
      return run('PATCH', path, body) as Promise<T>;
    },
    put<T>(path: string, body?: unknown): Promise<T> {
      return run('PUT', path, body) as Promise<T>;
    },
    delete<T>(path: string): Promise<T> {
      return run('DELETE', path) as Promise<T>;
    },
    _middleware: opts.middleware,
  };
  return client;
}

export { resolveBaseUrl };
